import { Injectable } from '@angular/core';
import { Map } from 'immutable';

import { TagAdapter } from '../core/adapters/tag.adapter';
import { ProjectStorage } from '../core/services/project.storage';
import { DcRouteUrlBuilder } from '../core/utils/dc-route-url-builder';
import {
	IFormulaUsage,
	IUsageInCarac,
	IUsageInDB,
	IUsageInSource,
	IFormula,
	IFormulaResponse,
} from '../requesters/formula/formula.model';
import { FormulaItemEntity } from './components/formula-list/formula-item.entity';
import { FormulaListEntity } from './components/formula-list/formula-list.entity';
import { FormulaEntity } from './components/formula-list/formula.entity';
import { FormulaUsageListEntity } from './components/formula-usage-list/formula-usage-list.entity';
import {
	FormulaUsageDetails,
	FormulaUsageEntity,
} from './components/formula-usage-list/formula-usage.entity';

const FORMULA_NAME_SEPARATOR = '.';

@Injectable()
export class FormulaParser {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		private readonly projectStorage: ProjectStorage
	) {}

	public parseFormulaUsages(
		response: ReadonlyArray<IFormulaUsage>
	): FormulaUsageListEntity {
		const list = this.generateFormulaListConfig();

		const tuples = response.reduce((acc, curr, idx) => {
			const [cat, ...trail] = curr.formula_name.split(FORMULA_NAME_SEPARATOR);
			const name = trail.reduce<string>((acc, curr) => {
				if (acc === '') {
					return curr;
				}
				return `${acc}${FORMULA_NAME_SEPARATOR}${curr}`;
			}, '');

			const usage = FormulaUsageEntity.build({
				id: idx,
				label: name,
				category: cat,
				count: curr.usage_count,
				isDeprecated: curr.deprecated,
				datablock: this.parseUsagesInDatablocks(curr.usages.usages_in_dbs),
				hdSource: this.parseUsagesInSources(curr.usages.usages_in_sources),
				entityCharacteristic: this.parseUsagesInEntityCharacteristic(
					curr.usages.usages_in_caracs
				),
				entityLink: this.parseUsageInEntityLinks(
					curr.usages.usages_in_entites_links
				),
			});
			acc.push([curr.formula_name, usage]);
			return acc;
		}, new Array<[string, FormulaUsageEntity]>());

		return list.setElements(Map(tuples));
	}

	public getAllFormulaUsageCount(
		response: ReadonlyArray<IFormulaUsage>
	): number {
		return response.reduce((total, usage) => total + usage.usage_count, 0);
	}

	public generateFormulaList(
		response: ReadonlyArray<IFormulaResponse>,
		currentUserId: string
	): FormulaListEntity {
		const list = FormulaListEntity.build([
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'dataType',
				alias: $localize`:i18n=@@formula.list.column.type:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@formula.list.column.label:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 2,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@formula.list.column.description:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 3,
				visible: false,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@formula.list.column.updateDate:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'updatedBy',
				alias: $localize`:i18n=@@formula.list.column.updatedBy:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 5,
			},
			{
				field: 'isShared',
				alias: $localize`:i18n=@@formula.list.column.shared:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 6,
			},
		]);

		const tuples = response.reduce((acc, curr) => {
			const entity = FormulaItemEntity.build({
				id: curr.id,
				label: curr.metadata?.label ?? '',
				isShared: curr.shared ?? false,
				description: curr.metadata?.description ?? '',
				tags: this.tagAdapter.generateResponseMetadataTag(
					curr.metadata?.tags ?? []
				),
				updateDate: curr.metadata?.update_date
					? new Date(curr.metadata.update_date)
					: '',
				updatedBy: curr.metadata?.updated_by ?? '',
				isOwner: curr.metadata?.created_by === currentUserId,
				isActive: curr.metadata?.actif,
				dataType: curr.formula.data_type ?? '',
				formula: this.generateFormulaEntity(curr.formula),
			});
			acc.push([`${curr.id}`, entity]);
			return acc;
		}, new Array<[string, FormulaItemEntity]>());

		return list.setElements(Map(tuples));
	}

	private parseUsagesInDatablocks(
		res: ReadonlyArray<IUsageInDB>
	): FormulaUsageDetails {
		return res.map((db) => {
			const step = $localize`:i18n=@@formula.usage.in.datablock.step:`;
			return {
				url: this.parseDatablockPageUrl(db.datablock_id),
				label: `${db.datablock_label} - ${step} : ${db.step_index}`,
			};
		});
	}

	private parseUsagesInSources(
		res: ReadonlyArray<IUsageInSource>
	): FormulaUsageDetails {
		return res.map((source) => {
			const step = $localize`:i18n=@@formula.usage.in.datablock.step:`;
			return {
				url: this.parseSourcePageUrl(source),
				label: `${source.source_label} - ${step} : ${source.step_index} - ${source.element_label}`,
			};
		});
	}

	private parseUsagesInEntityCharacteristic(
		res: ReadonlyArray<IUsageInCarac>
	): FormulaUsageDetails {
		return res.map((item) => ({
			url: this.parseEntityPageUrl(item.entite_type_id),
			label: `${item.carac_label} (${item.entite_type_label})`,
		}));
	}

	private parseUsageInEntityLinks(
		res: ReadonlyArray<IUsageInCarac>
	): FormulaUsageDetails {
		return res.map((item) => ({
			url: this.parseEntityPageUrl(item.entite_type_id),
			label: `${item.carac_label} (${item.entite_type_label})`,
		}));
	}

	private parseDatablockPageUrl(datablockId: number): string {
		const projectId = this.projectStorage.getValue()?.projectId;
		return DcRouteUrlBuilder.buildEditPageUrl(
			projectId,
			datablockId,
			'DATABLOCK'
		);
	}

	private parseEntityPageUrl(entityId: number): string {
		const projectId = this.projectStorage.getValue()?.projectId;
		return DcRouteUrlBuilder.buildEditPageUrl(
			projectId,
			entityId,
			'ENTITETYPE'
		);
	}

	private generateFormulaListConfig(): FormulaUsageListEntity {
		return FormulaUsageListEntity.build([
			{
				field: 'label',
				alias: $localize`:@@formula:`,
				type: 'string',
				isFilterable: true,
			},
			{
				field: 'count',
				alias: $localize`:@@formula.count:`,
				type: 'string',
				isFilterable: true,
			},
			{
				field: 'category',
				alias: $localize`:@@formula.category:`,
				type: 'string',
				groupIdx: 1,
				isFilterable: true,
			},
			{
				field: 'datablock',
				alias: $localize`:@@formula.usage.in.datablock:`,
				type: 'string',
			},
			{
				field: 'entityLink',
				alias: $localize`:@@formula.usage.in.entities.links:`,
				type: 'string',
			},
			{
				field: 'entityCharacteristic',
				alias: $localize`:@@formula.usage.in.entities:`,
				type: 'string',
			},
			{
				field: 'hdSource',
				alias: $localize`:@@formula.usage.in.hd:`,
				type: 'string',
			},
		]);
	}

	private parseSourcePageUrl(source: IUsageInSource): string {
		const projectId = this.projectStorage.getValue()?.projectId;

		const {
			type: elementType,
			element_id: elementId,
			sub_type: elementSubType,
		} = source;
		// FIXME: derive from state
		return DcRouteUrlBuilder.buildEditPageUrl(
			projectId,
			elementId,
			elementType,
			elementSubType
		);
	}

	private generateFormulaEntity(response: IFormula): FormulaEntity {
		return FormulaEntity.build({
			type: response.type,
			value:
				response.type === 'function' ? response.function_name : response.value,
			params: response.params.map((p) => this.generateFormulaEntity(p)),
		});
	}
}
